// Scoped styles for the booking UI (.bk). Injected once on import.
// Tone: light (paper) by default; dark automatically inside .on-dark, or forced with data-tone.
const css = `
.v2 .bk {
  --bk-fg: #241E61;
  --bk-ink: #232047;
  --bk-muted: #67648F;
  --bk-line: #E4E3F0;
  --bk-line-strong: #CFCDE3;
  --bk-surface: #FFFFFF;
  --bk-field: #FFFFFF;
  --bk-soft: #F7F7FC;
  --bk-sel-bg: #241E61;
  --bk-sel-fg: #FFFFFF;
  --bk-accent: #2F9E58;
  --bk-hover: #F1F0F9;
  --bk-error: #C2352B;
  --bk-error-soft: #FDF1F0;
  --bk-warn-soft: #FBF7E8;
  --bk-warn-fg: #6B5310;
  color: var(--bk-ink);
}
.v2 .on-dark .bk:not([data-tone='light']),
.v2 .bk[data-tone='dark'] {
  --bk-fg: #FFFFFF;
  --bk-ink: rgba(255,255,255,.92);
  --bk-muted: rgba(255,255,255,.68);
  --bk-line: rgba(255,255,255,.16);
  --bk-line-strong: rgba(255,255,255,.32);
  --bk-surface: rgba(255,255,255,.045);
  --bk-field: rgba(255,255,255,.06);
  --bk-soft: rgba(255,255,255,.06);
  --bk-sel-bg: #CFF710;
  --bk-sel-fg: #241E61;
  --bk-accent: #CFF710;
  --bk-hover: rgba(255,255,255,.1);
  --bk-error: #FFB4A8;
  --bk-error-soft: rgba(255,120,100,.12);
  --bk-warn-soft: rgba(207,247,16,.08);
  --bk-warn-fg: #E9F99A;
}
.v2 .bk :focus-visible { outline: 2px solid var(--bk-accent); outline-offset: 2px; }

/* card */
.v2 .bk-card {
  background: var(--bk-surface);
  border-radius: 22px;
  box-shadow: 0 0 0 1px var(--bk-line), 0 30px 60px -36px rgba(24,19,67,.32);
}
.v2 .bk[data-tone='dark'] .bk-card, .v2 .on-dark .bk:not([data-tone='light']) .bk-card { box-shadow: 0 0 0 1px var(--bk-line); }

.v2 .bk-autoh { transition: height .24s var(--bm-ease); overflow: hidden; }
.v2 .bk-step-in { animation: bk-in .22s var(--bm-ease); }
@keyframes bk-in { from { transform: translateX(10px); } to { transform: none; } }

.v2 .bk-h { color: var(--bk-fg); font-weight: 700; letter-spacing: -0.02em; }
.v2 .bk-muted { color: var(--bk-muted); }
.v2 .bk-label { display:block; font-size: 14px; font-weight: 600; color: var(--bk-fg); margin-bottom: 6px; }
.v2 .bk-label .opt { font-weight: 400; color: var(--bk-muted); }
.v2 .bk-mono { font-family: 'Geist Mono', ui-monospace, monospace; letter-spacing: .08em; text-transform: uppercase; font-size: 11.5px; }

/* fields */
.v2 .bk-input {
  width: 100%; height: 50px; padding: 0 14px;
  font-size: 16px; color: var(--bk-fg);
  background: var(--bk-field);
  border-radius: 12px; border: 0;
  box-shadow: inset 0 0 0 1px var(--bk-line-strong);
  transition: box-shadow .15s var(--bm-ease);
  appearance: none; -webkit-appearance: none;
}
.v2 .bk-input::placeholder { color: var(--bk-muted); opacity: .75; }
.v2 .bk-input:hover { box-shadow: inset 0 0 0 1px var(--bk-muted); }
.v2 .bk-input:focus { outline: none; box-shadow: inset 0 0 0 2px var(--bk-accent); }
.v2 .bk-input[aria-invalid='true'] { box-shadow: inset 0 0 0 2px var(--bk-error); }
.v2 select.bk-input {
  padding-right: 40px; cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5 6 6.5l5-5' fill='none' stroke='%2367648F' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 15px center;
}
.v2 select.bk-input option { color: #232047; background: #fff; }
.v2 .bk-err { display:flex; gap:6px; align-items:flex-start; margin-top: 6px; font-size: 13.5px; color: var(--bk-error); }
.v2 .bk-hint { margin-top: 6px; font-size: 13.5px; color: var(--bk-muted); }
.v2 .bk-note { display:flex; gap:8px; align-items:flex-start; margin-top: 8px; padding: 9px 12px; border-radius: 10px; font-size: 13.5px; line-height: 1.45; background: var(--bk-warn-soft); color: var(--bk-warn-fg); }

/* chips */
.v2 .bk-chip {
  display:inline-flex; align-items:center; gap:8px;
  min-height: 42px; padding: 0 15px; border-radius: 999px;
  font-size: 14.5px; font-weight: 500; color: var(--bk-fg);
  background: var(--bk-field);
  box-shadow: inset 0 0 0 1px var(--bk-line-strong);
  transition: background-color .15s var(--bm-ease), color .15s var(--bm-ease), box-shadow .15s var(--bm-ease);
  cursor: pointer; user-select: none;
}
.v2 .bk-chip:hover { box-shadow: inset 0 0 0 1px var(--bk-fg); }
.v2 .bk-chip[aria-pressed='true'], .v2 .bk-chip[aria-checked='true'] { background: var(--bk-sel-bg); color: var(--bk-sel-fg); box-shadow: inset 0 0 0 1px var(--bk-sel-bg); }
.v2 .bk-chip .tick { width: 14px; height: 14px; margin-left: -3px; }

/* progress */
.v2 .bk-prog { display:grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.v2 .bk-prog button { text-align:left; padding: 0; background: none; border: 0; cursor: default; }
.v2 .bk-prog button:not(:disabled) { cursor: pointer; }
.v2 .bk-prog .track { display:block; height: 4px; border-radius: 4px; background: var(--bk-line); overflow: hidden; }
.v2 .bk-prog .fill { display:block; height: 100%; width: 100%; background: var(--bm-gradient); transform-origin: left; transform: scaleX(0); transition: transform .4s var(--bm-ease); }
.v2 .bk-prog .on .fill { transform: scaleX(1); }
.v2 .bk-prog .lbl { display:block; margin-top: 8px; font-size: 12px; white-space: nowrap; font-weight: 500; color: var(--bk-muted); }
.v2 .bk-prog .cur .lbl { color: var(--bk-fg); font-weight: 600; }

/* date grid */
.v2 .bk-cal { width: 100%; border-collapse: separate; border-spacing: 0; }
.v2 .bk-cal-head { display:grid; grid-template-columns: repeat(7, 1fr); }
.v2 .bk-cal-head span { text-align:center; font-size: 11.5px; font-weight: 600; color: var(--bk-muted); padding-bottom: 8px; letter-spacing: .04em; }
.v2 .bk-cal-row { display:grid; grid-template-columns: repeat(7, 1fr); }
.v2 .bk-day {
  position: relative; display:flex; flex-direction: column; align-items:center; justify-content:center;
  aspect-ratio: 1 / 1; max-height: 52px; width: 100%; margin: 2px 0;
  border-radius: 12px; border: 0; background: none;
  font-size: 15px; font-weight: 400; color: var(--bk-muted); opacity: .5;
  font-variant-numeric: tabular-nums;
}
.v2 .bk-day.avail { opacity: 1; color: var(--bk-fg); font-weight: 600; cursor: pointer; background: var(--bk-soft); }
.v2 .bk-day.avail:hover { background: var(--bk-hover); box-shadow: inset 0 0 0 1px var(--bk-fg); }
.v2 .bk-day.avail::after { content:''; position:absolute; bottom: 6px; width: 4px; height: 4px; border-radius: 50%; background: var(--bk-accent); }
.v2 .bk-day.today { box-shadow: inset 0 0 0 1px var(--bk-line-strong); }
.v2 .bk-day[aria-selected='true'] { background: var(--bk-sel-bg); color: var(--bk-sel-fg); box-shadow: none; }
.v2 .bk-day[aria-selected='true']::after { background: currentColor; }
.v2 .bk-day .mon { position:absolute; top: 3px; font-size: 9.5px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; opacity: .8; }

/* date strip (compact) */
.v2 .bk-strip { display:flex; gap: 8px; overflow-x: auto; scroll-snap-type: x mandatory; scrollbar-width: none; padding: 2px; margin: -2px; }
.v2 .bk-strip::-webkit-scrollbar { display: none; }
.v2 .bk-strip { -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 28px), transparent); mask-image: linear-gradient(to right, #000 calc(100% - 28px), transparent); padding-right: 28px; }
.v2 .bk-times.bk-times-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.v2 .bk-times.bk-times-4 .bk-time { font-size: 14.5px; }
.v2 .bk-sday {
  flex: 0 0 auto; scroll-snap-align: start; width: 64px; padding: 9px 0 10px;
  display:flex; flex-direction:column; align-items:center; gap: 1px;
  border-radius: 14px; border: 0; cursor: pointer;
  background: var(--bk-field); color: var(--bk-fg);
  box-shadow: inset 0 0 0 1px var(--bk-line-strong);
  transition: background-color .15s var(--bm-ease), color .15s var(--bm-ease);
}
.v2 .bk-sday:hover { box-shadow: inset 0 0 0 1px var(--bk-fg); }
.v2 .bk-sday .wd { font-size: 11.5px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--bk-muted); }
.v2 .bk-sday .dn { font-size: 21px; font-weight: 700; line-height: 1.15; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; }
.v2 .bk-sday .mo { font-size: 11.5px; color: var(--bk-muted); }
.v2 .bk-sday[aria-pressed='true'] { background: var(--bk-sel-bg); color: var(--bk-sel-fg); box-shadow: none; }
.v2 .bk-sday[aria-pressed='true'] .wd, .v2 .bk-sday[aria-pressed='true'] .mo { color: inherit; opacity: .8; }

/* time slots */
.v2 .bk-times { display:grid; grid-template-columns: repeat(auto-fill, minmax(92px, 1fr)); gap: 8px; }
.v2 .bk-time {
  height: 46px; border-radius: 12px; border: 0; cursor: pointer;
  font-size: 15px; font-weight: 600; color: var(--bk-fg); font-variant-numeric: tabular-nums;
  background: var(--bk-field); box-shadow: inset 0 0 0 1px var(--bk-line-strong);
  transition: background-color .15s var(--bm-ease), color .15s var(--bm-ease), box-shadow .15s var(--bm-ease);
}
.v2 .bk-time:hover { box-shadow: inset 0 0 0 1.5px var(--bk-fg); }
.v2 .bk-time[aria-pressed='true'] { background: var(--bk-sel-bg); color: var(--bk-sel-fg); box-shadow: none; }

/* sticky action bar on phones */
@media (max-width: 639px) {
  .v2 .bk-foot-sticky { position: sticky; bottom: 0; z-index: 5; background: var(--bk-surface); margin-left: -24px; margin-right: -24px; padding-left: 24px; padding-right: 24px; padding-bottom: 16px; box-shadow: 0 -12px 24px -18px rgba(24,19,67,.35); }
}

/* skeletons */
.v2 .bk-skel { background: var(--bk-soft); border-radius: 12px; animation: bk-pulse 1.2s ease-in-out infinite; }
@keyframes bk-pulse { 50% { opacity: .55; } }

/* buttons inside the booking UI */
.v2 .bk-btn {
  display:inline-flex; align-items:center; justify-content:center; gap: 8px;
  min-height: 52px; padding: 0 24px; border-radius: 999px; border: 0; cursor: pointer;
  font-size: 16px; font-weight: 600; letter-spacing: -0.005em;
  background: var(--bk-fg); color: var(--bk-sel-fg);
  transition: transform .15s var(--bm-ease), background-color .15s var(--bm-ease), opacity .15s;
}
.v2 .bk-btn.go { background: var(--bm-gradient); color: #241E61; }
.v2 .bk-btn:hover { filter: brightness(1.08); }
.v2 .bk-btn:active { transform: translateY(1px); }
.v2 .bk-btn[aria-disabled='true'] { opacity: .45; cursor: not-allowed; }
.v2 .bk[data-tone='dark'] .bk-btn:not(.go), .v2 .on-dark .bk:not([data-tone='light']) .bk-btn:not(.go) { background: #fff; color: #241E61; }
.v2 .bk-link { background:none; border:0; padding:0; cursor:pointer; color: var(--bk-fg); font-weight: 600; text-decoration: underline; text-decoration-color: var(--bk-line-strong); text-underline-offset: 3px; }
.v2 .bk-link:hover { text-decoration-color: currentColor; }
.v2 .bk-ghost {
  display:inline-flex; align-items:center; gap: 8px; min-height: 44px; padding: 0 16px; border-radius: 999px; border:0; cursor:pointer;
  font-size: 14.5px; font-weight: 600; color: var(--bk-fg); background: transparent; box-shadow: inset 0 0 0 1px var(--bk-line-strong);
}
.v2 .bk-ghost:hover { box-shadow: inset 0 0 0 1.5px var(--bk-fg); }
.v2 .bk-when { margin-top: 14px; padding-left: 14px; border-left: 3px solid var(--bk-accent); }
.v2 .bk-when-day { font-size: 19px; font-weight: 700; letter-spacing: -0.015em; color: var(--bk-fg); }
.v2 .bk-when-time { margin-top: 2px; font-size: 17px; font-weight: 600; color: var(--bk-fg); font-variant-numeric: tabular-nums; }
.v2 .bk-when-where { margin-top: 6px; display:flex; align-items:center; gap: 8px; font-size: 14.5px; color: var(--bk-muted); }
.v2 .bk-panel { margin-top: 24px; padding: 18px 18px 16px; border-radius: 16px; background: var(--bk-soft); }
.v2 .bk-ghost.solid { background: var(--bk-fg); color: var(--bk-sel-fg); box-shadow: none; }
.v2 .bk-ghost.solid:hover { filter: brightness(1.08); }

.v2 .bk-alert { display:flex; gap: 10px; align-items:flex-start; padding: 12px 14px; border-radius: 12px; font-size: 14.5px; line-height: 1.45; background: var(--bk-error-soft); color: var(--bk-fg); }
.v2 .bk-alert svg { color: var(--bk-error); flex: none; margin-top: 2px; }

.v2 .bk-spin { width: 18px; height: 18px; border-radius: 50%; border: 2px solid currentColor; border-right-color: transparent; animation: bk-rot .7s linear infinite; }
@keyframes bk-rot { to { transform: rotate(360deg); } }

/* confirmation tick */
.v2 .bk-tick path { stroke-dasharray: 40; stroke-dashoffset: 40; animation: bk-draw .5s var(--bm-ease) .15s forwards; }
@keyframes bk-draw { to { stroke-dashoffset: 0; } }

.v2 .bk-hp { position:absolute !important; left:-10000px !important; width:1px; height:1px; overflow:hidden; }

/* pick layout: side by side when the card is wide */
.v2 .bk-pick { container-type: inline-size; }
.v2 .bk-pick-grid { display:grid; gap: 22px; }
@container (min-width: 480px) {
  .v2 .bk-pick-grid { grid-template-columns: minmax(0, 1fr) 172px; gap: 26px; }
  .v2 .bk-pick-grid .bk-times { grid-template-columns: 1fr; max-height: 352px; overflow-y: auto; padding: 2px; margin: -2px; scrollbar-width: thin; }
}

@media (prefers-reduced-motion: reduce) {
  .v2 .bk-step-in { animation: none; }
  .v2 .bk-autoh { transition: none; }
  .v2 .bk-tick path { animation: none; stroke-dashoffset: 0; }
}
`;

if (typeof document !== 'undefined') {
  let s = document.getElementById('bk-css');
  if (!s) {
    s = document.createElement('style');
    s.id = 'bk-css';
    document.head.appendChild(s);
  }
  s.textContent = css;
}

export {};
